import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import './AuthForm.css';
import { FaArrowLeft } from 'react-icons/fa6';
import Preloader from './Preloader';

function PendingShelterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shelter, setShelter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('pending');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    document.title = "Pending Shelter Details";
  }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
    fetchShelter();
    // eslint-disable-next-line
  }, [id]);

  async function fetchShelter() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('temp_shelter')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      setError('Failed to load shelter details');
      setShelter(null);
    } else {
      setShelter(data);
      setStatus(data.status);
    }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    const { error } = await supabase
      .from('temp_shelter')
      .update({ status: status })
      .eq('id', id);
    if (error) {
      setSaveMsg('Failed to save changes.');
    } else {
      setSaveMsg('Status updated!');
      fetchShelter();
    }
    setSaving(false);
  }

  if (loading || !currentUser) return <Preloader text="Loading Details..." />;
  if (error || !shelter) return <div style={{ color: '#d32f2f', padding: 40 }}>{error || 'Shelter not found.'}</div>;

  const canEdit = currentUser && (
    currentUser.role === 'admin' ||
    currentUser.role === 'super admin'
  );

  const imageUrls = [shelter.house_image1, shelter.house_image2, shelter.house_image3, shelter.house_image4].filter(Boolean);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: 32 }}>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaArrowLeft />
          </button>
          <ProfileMenu />
        </div>
        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 8px #0001' }}>
          {/* Title row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h1 style={{ fontSize: 24, margin: 0 }}>{shelter.house_name}</h1>
            {canEdit ? (
              <div>
                <label style={{ color: '#666', marginRight: 8 }}>Status:</label>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            ) : (
              <div>
                <label style={{ color: '#666', marginRight: 8 }}>Status:</label>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>{shelter.status}</span>
              </div>
            )}
          </div>
          {/* Main content: two-column layout */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
            {/* Left column: Images */}
            <div style={{ flex: '2 1 500px', minWidth: 300 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                {imageUrls.length > 0 ? (
                  imageUrls.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`shelter-img-${i + 1}`}
                      style={{
                        flex: '1 1 auto',
                        maxWidth: imageUrls.length > 1 ? 'calc(50% - 8px)' : '100%',
                        height: 'auto',
                        aspectRatio: '1 / 1',
                        borderRadius: 12,
                        objectFit: 'cover',
                        background: '#eee',
                      }}
                    />
                  ))
                ) : (
                  <div style={{
                    width: '100%',
                    height: 300,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f0f0f0',
                    color: '#888'
                  }}>
                    No Images Provided
                  </div>
                )}
              </div>
            </div>
            {/* Right column: Details */}
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Household Name</label>
                  <span style={{ fontSize: 16 }}>
                    {shelter.house_name || 'N/A'}
                  </span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Address</label>
                  <span style={{ fontSize: 16 }}>{shelter.house_addr || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Barangay</label>
                  <span style={{ fontSize: 16 }}>{shelter.barangay || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Contact Number</label>
                  <span style={{ fontSize: 16 }}>{shelter.contact_no || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Current Members</label>
                  <span style={{ fontSize: 16 }}>{shelter.members || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Shelter Capacity</label>
                  <span style={{ fontSize: 16 }}>{shelter.capacity || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Provider Name</label>
                  <span style={{ fontSize: 16 }}>{shelter.provider_name || 'N/A'}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Submitted On</label>
                  <span style={{ fontSize: 16 }}>{new Date(shelter.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <label style={{ color: '#666', marginBottom: 4, display: 'block' }}>Coordinates</label>
                  <span style={{ fontSize: 16 }}>
                    {shelter.latitude && shelter.longitude 
                      ? `${shelter.latitude.toFixed(6)}, ${shelter.longitude.toFixed(6)}`
                      : 'N/A'
                    }
                  </span>
                </div>
                {/* Action buttons */}
                {canEdit && (
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                    <button
                      onClick={handleSave}
                      style={{
                        background: '#19c37d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '12px 24px',
                        fontWeight: 'bold',
                        fontSize: 16
                      }}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => navigate(-1)}
                      style={{
                        padding: '12px 24px',
                        borderRadius: 8,
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 16
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {saveMsg && (
                  <div style={{ color: saveMsg.includes('Failed') ? '#d32f2f' : '#19c37d', marginTop: 12 }}>
                    {saveMsg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PendingShelterDetail; 